import { isPlatformBrowser } from '@angular/common';

/**
 * Utility helpers for generating stable fragment identifiers across the app.
 */
export interface FragmentContext {
    heading?: string | null;
    section?: string | null;
    route?: string | null;
    groupPosition?: string | number | null;
    componentPosition?: string | number | null;
    itemPosition?: string | number | null;
    fallbackIndex?: number | null;
}

export interface FragmentHighlightOptions {
    highlightText?: string;
    highlightClass?: string;
    highlightDurationMs?: number;
}

/**
 * Converts a value into a URL-safe slug.
 */
export function slugifyFragment(value: string): string {
    return value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .replace(/-{2,}/g, '-');
}

/**
 * Builds a deterministic fragment id that combines semantic information (heading / section)
 * with positional hints to avoid clashes when headings repeat. Returns null when no input
 * data is provided.
 */
export function createFragmentId(context: FragmentContext): string | null {
    const parts: string[] = [];

    const pushPart = (value: string | number | null | undefined) => {
        if (value === null || value === undefined) {
            return;
        }
        const str = typeof value === 'number' ? String(value) : value.trim();
        if (!str) {
            return;
        }
        parts.push(str);
    };

    pushPart(context.heading);
    if (context.section && context.section !== context.heading) {
        pushPart(context.section);
    }
    pushPart(context.route);
    pushPart(context.groupPosition);
    pushPart(context.componentPosition);
    pushPart(context.itemPosition);
    pushPart(context.fallbackIndex);

    if (!parts.length) {
        return null;
    }

    const slug = slugifyFragment(parts.join('-'));
    return slug || null;
}

/**
 * Tries to scroll to a DOM element matching the given fragment id while accounting for
 * the fixed header and breadcrumb heights. Does nothing on the server.
 */
export function scrollToFragmentId(fragment: string, platformId?: Object, options?: FragmentHighlightOptions): void {
    if (!fragment) {
        return;
    }

    if (platformId && !isPlatformBrowser(platformId)) {
        return;
    }

    if (typeof document === 'undefined') {
        return;
    }

    const target = document.getElementById(fragment);
    if (!target) {
        return;
    }

    const headerHeight = document.querySelector('#otb-header')?.clientHeight || 0;
    const breadcrumbHeight = document.querySelector('.breadcrumb-ul')?.clientHeight || 0;
    const offset = headerHeight + breadcrumbHeight + 40;

    const rect = target.getBoundingClientRect();
    const pageYOffset = (typeof window !== 'undefined' && typeof window.pageYOffset === 'number') ? window.pageYOffset : 0;
    const scrollTo = Math.max(rect.top + pageYOffset - offset, 0);

    try {
        if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
            window.scrollTo({ top: scrollTo, behavior: 'smooth' });
        }
    } catch (e) { }

    const highlightText = options?.highlightText?.trim();
    if (highlightText) {
        const highlightClass = options?.highlightClass || 'search-fragment-highlight';
        const duration = options?.highlightDurationMs ?? 3000;
        removeExistingHighlights(target, highlightClass);
        highlightFirstMatch(target, highlightText, highlightClass, duration);
    }
}

function highlightFirstMatch(target: HTMLElement, searchText: string, highlightClass: string, durationMs: number): void {
    const regex = new RegExp(escapeRegExp(searchText), 'i');
    const walker = document.createTreeWalker(target, NodeFilter.SHOW_TEXT);
    let node = walker.nextNode();

    while (node) {
        const text = node.textContent ?? '';
        const match = regex.exec(text);

        if (match) {
            const range = document.createRange();
            range.setStart(node, match.index);
            range.setEnd(node, match.index + match[0].length);

            const mark = document.createElement('mark');
            mark.className = highlightClass;

            const contents = range.extractContents();
            mark.appendChild(contents);
            range.insertNode(mark);

            if (durationMs > 0) {
                setTimeout(() => removeHighlight(mark), durationMs);
            }
            break;
        }

        node = walker.nextNode();
    }
}

function removeExistingHighlights(root: HTMLElement, highlightClass: string): void {
    const existing = root.querySelectorAll(`mark.${highlightClass}`);
    existing.forEach(mark => removeHighlight(mark as HTMLElement));
}

function removeHighlight(mark: HTMLElement): void {
    const parent = mark.parentNode;
    if (!parent) {
        return;
    }

    const text = mark.textContent ?? '';
    parent.replaceChild(document.createTextNode(text), mark);
    parent.normalize();
}

function escapeRegExp(value: string): string {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Highlights the first occurrence of search text on the entire page (not just within a specific element).
 * Used when navigating to a page without a specific fragment but wanting to highlight matching text.
 */
export function highlightTextOnPage(searchText: string, platformId?: Object, options?: { highlightClass?: string; highlightDurationMs?: number }): void {
    if (!searchText || !searchText.trim()) {
        return;
    }

    if (platformId && !isPlatformBrowser(platformId)) {
        return;
    }

    if (typeof document === 'undefined') {
        return;
    }

    // Target the main content area to avoid highlighting in header/footer
    const mainContent = document.querySelector('main') || document.querySelector('.content') || document.querySelector('.page-content') || document.body;

    if (!mainContent) {
        return;
    }

    const highlightClass = options?.highlightClass || 'search-fragment-highlight';
    const duration = options?.highlightDurationMs ?? 3000;

    // Remove any existing highlights first
    removeExistingHighlights(mainContent as HTMLElement, highlightClass);

    // Highlight the first match
    highlightFirstMatch(mainContent as HTMLElement, searchText.trim(), highlightClass, duration);
}
